import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Search, Crown, TrendingUp } from 'lucide-react';
import MonitorCard from './MonitorCard';
import MonitorForm from './MonitorForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MonitorList = ({ token }) => {
  const [monitors, setMonitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingMonitor, setEditingMonitor] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [subscriptionLimits, setSubscriptionLimits] = useState(null);

  useEffect(() => {
    fetchMonitors();
    fetchSubscriptionLimits();
  }, []);

  const fetchSubscriptionLimits = async () => {
    try {
      const response = await axios.get(`${API}/subscription/limits`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionLimits(response.data);
    } catch (err) {
      console.error('Failed to fetch subscription limits:', err);
    }
  };

  const fetchMonitors = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/monitors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter out disabled monitors for the main display
      const activeMonitors = response.data.filter(monitor => monitor.status !== 'disabled');
      setMonitors(activeMonitors);
    } catch (err) {
      setError('Failed to load monitors');
      console.error('Monitors error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (monitorData) => {
    try {
      const response = await axios.post(`${API}/monitors`, monitorData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMonitors([...monitors, response.data]);
      setShowForm(false);
      // Refresh subscription limits after creating a monitor
      fetchSubscriptionLimits();
    } catch (err) {
      throw new Error(err.response?.data?.detail || 'Failed to create monitor');
    }
  };

  const handleUpdate = async (monitorId, monitorData) => {
    try {
      const response = await axios.put(`${API}/monitors/${monitorId}`, monitorData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMonitors(monitors.map(m => m.id === monitorId ? response.data : m));
      setEditingMonitor(null);
    } catch (err) {
      throw new Error(err.response?.data?.detail || 'Failed to update monitor');
    }
  };

  const handleDelete = async (monitor) => {
    if (!window.confirm(`Are you sure you want to delete "${monitor.name}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/monitors/${monitor.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMonitors(monitors.filter(m => m.id !== monitor.id));
      // Refresh subscription limits after deleting a monitor
      fetchSubscriptionLimits();
    } catch (err) {
      console.error('Delete error:', err);
      alert('Failed to delete monitor');
    }
  };

  const handleToggleStatus = async (monitor) => {
    try {
      const newStatus = monitor.status === 'active' ? 'paused' : 'active';
      const response = await axios.put(`${API}/monitors/${monitor.id}`, 
        { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMonitors(monitors.map(m => m.id === monitor.id ? response.data : m));
    } catch (err) {
      console.error('Status toggle error:', err);
      alert('Failed to update monitor status');
    }
  };

  const filteredMonitors = monitors.filter(monitor =>
    monitor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    monitor.url.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 xl:p-10">
        <div className="animate-pulse">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-gray-200 h-48 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 xl:p-10">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 sm:mb-8 space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Monitors</h2>
          <p className="text-gray-600 text-sm sm:text-base">Manage your website monitoring</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center justify-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base"
        >
          <Plus className="w-4 h-4" />
          <span>Add Monitor</span>
        </button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search monitors..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 sm:pl-10 pr-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm sm:text-base"
          />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 text-sm sm:text-base">
          {error}
        </div>
      )}

      {/* Subscription Status Banner */}
      {subscriptionLimits && (
        <div className="mb-6">
          {subscriptionLimits.at_limit ? (
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start space-x-4">
                <div className="bg-amber-100 p-2 rounded-lg">
                  <Crown className="w-6 h-6 text-amber-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-amber-800 mb-1">Monitor Limit Reached</h3>
                  <p className="text-amber-700 text-sm mb-3">
                    You're using all {subscriptionLimits.limits.monitors} monitors on your {subscriptionLimits.tier} plan. 
                    Upgrade to monitor more websites and get advanced features.
                  </p>
                  <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                    <button
                      onClick={() => window.location.href = '/settings?section=subscription'}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                    >
                      <Crown className="w-4 h-4 inline mr-2" />
                      Upgrade Plan
                    </button>
                    <button
                      onClick={() => window.location.href = '/settings?section=subscription'}
                      className="bg-white text-blue-600 border border-blue-200 px-4 py-2 rounded-lg hover:bg-blue-50 transition-colors text-sm font-medium"
                    >
                      View Pricing
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : subscriptionLimits.usage.monitors >= subscriptionLimits.limits.monitors * 0.8 ? (
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start space-x-4">
                <div className="bg-blue-100 p-2 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-blue-800 mb-1">Approaching Monitor Limit</h3>
                  <p className="text-blue-700 text-sm mb-3">
                    You're using {subscriptionLimits.usage.monitors} of {subscriptionLimits.limits.monitors} monitors on your {subscriptionLimits.tier} plan. 
                    Consider upgrading to monitor more websites.
                  </p>
                  <button
                    onClick={() => window.location.href = '/settings?section=subscription'}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                  >
                    View Upgrade Options
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      )}

      {/* Monitors Grid */}
      {filteredMonitors.length === 0 ? (
        <div className="text-center py-8 sm:py-12">
          <div className="bg-gray-100 w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <Plus className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
          </div>
          <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No monitors yet</h3>
          <p className="text-gray-600 mb-6 text-sm sm:text-base">Add your first website to start monitoring</p>
          <button
            onClick={() => setShowForm(true)}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base"
          >
            Add Your First Monitor
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {filteredMonitors.map((monitor) => (
            <MonitorCard
              key={monitor.id}
              monitor={monitor}
              onEdit={setEditingMonitor}
              onDelete={handleDelete}
              onToggleStatus={handleToggleStatus}
            />
          ))}
        </div>
      )}

      {/* Monitor Form Modal */}
      {(showForm || editingMonitor) && (
        <MonitorForm
          monitor={editingMonitor}
          token={token}
          onSubmit={editingMonitor ? 
            (data) => handleUpdate(editingMonitor.id, data) : 
            handleCreate
          }
          onClose={() => {
            setShowForm(false);
            setEditingMonitor(null);
          }}
        />
      )}
    </div>
  );
};

export default MonitorList;
